import { useCallback, useState } from 'react';
import { Mail, QrCode, Smartphone } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Logo } from '@nsibi/ui-web';

import { AgeRefused } from '../components/AgeRefused';
import { EmailSignIn } from '../components/EmailSignIn';
import { GoogleSignIn } from '../components/GoogleSignIn';
import { PinStep } from '../components/PinStep';
import { QrSignIn } from '../components/QrSignIn';
import { useAuthMethods } from '../lib/useAuthMethods';
import type { Session } from '../lib/session';
import { coinShopUrl } from '../lib/coinShop';
import { isCardPurchase, safeNext } from '../lib/returnTo';
import { isPhoneBrowser } from '../lib/phoneBrowser';
import { useTranslation } from "react-i18next";

/**
 * The doors: the QR code, then - under one "or", as on the phone's number
 * screen - "Continue with Google" and "Continue with email", each drawn
 * only once the server has said it is on. On a PHONE, where the app usually lives on
 * the same device and there is nothing to scan with, the code leads as
 * "Open Nsibi" (the app approves the same code, `lib/phoneBrowser.ts`),
 * with the QR code itself one tap away for somebody with a second phone.
 *
 * There is NO phone-number sign-in on the web (the owner, 2026-10-21). It
 * came back for phones on 2026-10-19 and went again: an SMS code proves
 * control of a number, which is exactly what a SIM swap also proves (see
 * the registration-lock note in `auth_phone/views.py`), and a session made
 * that way has no linked device behind it, so it carries no room keys from
 * a phone. A scanned code is approved BY the phone, which is the point.
 * The component lives in git history (`components/PhoneSignIn.tsx`).
 *
 * Email and Google are the same trade, behind staff switches
 * (`email_login_enabled`, `google_login_enabled`), drawn only once
 * `GET /v1/auth/methods/` has said they are on. Google is the app's own
 * provider (`/v1/auth/google/verify/`), signing in as the web client.
 *
 * `?next=` is where to land after signing in (`lib/returnTo.ts`); the coin
 * shop's card hand-off uses it to come back to the card checkout.
 */
export function SignIn({ onSignedIn }: { onSignedIn: (s: Session) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const buying = isCardPurchase(next);
  const { methods, refresh } = useAuthMethods();
  const [mode, setMode] = useState<'scan' | 'email' | 'google-pin' | 'refused'>('scan');
  const [googleLock, setGoogleLock] = useState<{ challenge: string; retryAfter: number } | null>(
    null,
  );
  const [phone] = useState(() => isPhoneBrowser());
  const [showCode, setShowCode] = useState(false);

  // Move first, then hand over the session: both are state updates in one
  // tick, so the signed-in app renders once, already at `next`, instead of
  // landing on its default route and then moving.
  const signedIn = useCallback(
    (s: Session) => {
      if (next) navigate(next, { replace: true });
      onSignedIn(s);
    },
    [next, navigate, onSignedIn],
  );

  // The other doors. Google and email are the phone's pair, full width under
  // an "or"; the QR code's own alternatives (on a phone) sit under them.
  const showingCode = mode === 'scan' && (!phone || showCode);
  const others = methods.google || (methods.email && mode !== 'email');
  const doors = (
    <div className="mx-auto mt-8 w-full max-w-sm">
      {others ? (
        <>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            {t('auth.google.or', { defaultValue: 'or' })}
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="mt-4 flex flex-col items-center gap-3">
            {methods.google ? (
              <GoogleSignIn
                onSignedIn={signedIn}
                onLock={(challenge, retryAfter) => {
                  setGoogleLock({ challenge, retryAfter });
                  setMode('google-pin');
                }}
                onRefused={() => setMode('refused')}
                onSwitchOff={refresh}
              />
            ) : null}
            {/* Drawn only once the server has said email is on: hidden by
                default, so a server without it looks exactly as it did. */}
            {methods.email && mode !== 'email' ? (
              <Button
                variant="outline"
                className="h-10 w-full max-w-[400px]"
                onClick={() => setMode('email')}
              >
                <Mail />
                {t('auth.email.continueWithEmail', { defaultValue: 'Continue with email' })}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {/* On a computer the email form carries its own "Scan a code
            instead"; a phone's way back is Open Nsibi. */}
        {(mode === 'email' && phone) || (phone && showCode) ? (
          <Button variant="ghost" size="sm" onClick={() => { setMode('scan'); setShowCode(false); }}>
            {phone ? <Smartphone /> : <QrCode />}
            {phone
              ? t('routes.openNsibiShort', { defaultValue: 'Open Nsibi' })
              : t('routes.qrCode', { defaultValue: 'QR code' })}
          </Button>
        ) : null}
        {phone && !showingCode ? (
          <Button variant="ghost" size="sm" onClick={() => { setMode('scan'); setShowCode(true); }}>
            <QrCode />
            {t('routes.showQrCode', { defaultValue: 'Show QR code' })}
          </Button>
        ) : null}
      </div>
    </div>
  );

  const title = buying ? t('routes.cardSignInTitle', { defaultValue: 'Sign in to pay by card' }) : undefined;

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-muted/40 px-6 py-12">
      {/* The code on a computer is a two-column card; everything else is one
          narrow column, a phone's button included - stretched across a wide
          window it read as a banner. */}
      <div className={showingCode && !phone ? 'w-full max-w-3xl' : 'w-full max-w-sm'}>
        <Logo />

        <div className="mt-8">
          {mode === 'refused' ? (
            <AgeRefused />
          ) : mode === 'google-pin' && googleLock ? (
            <PinStep
              identity={{ provider: 'google' }}
              challenge={googleLock.challenge}
              retryAfter={googleLock.retryAfter}
              onSignedIn={signedIn}
              onBack={() => {
                setGoogleLock(null);
                setMode('scan');
              }}
              onExpired={() => {
                setGoogleLock(null);
                setMode('scan');
              }}
              backLabel={t('auth.web.otherWaysIn', { defaultValue: 'Other ways to sign in' })}
            />
          ) : mode === 'email' ? (
            <EmailSignIn
              onSignedIn={signedIn}
              onUseScan={() => setMode('scan')}
              onSwitchOff={refresh}
            />
          ) : (
            <QrSignIn onSignedIn={signedIn} title={title} phone={phone && !showCode} />
          )}
        </div>

        {mode === 'google-pin' || mode === 'refused' ? null : doors}

        {/* Here only to buy coins with mobile money? That needs no sign-in.
            Not said to somebody the shop just sent here to pay by card. */}
        {!buying && (
          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t('routes.justBuyingCoins', { defaultValue: 'Only here to buy coins?' })}{' '}
            <a href={coinShopUrl()} className="font-medium text-primary underline-offset-4 hover:underline">
              {t('routes.buyCoinsNoSignIn', { defaultValue: 'Buy them without signing in' })}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
